import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useJeMobil } from "../lib/mobil";

/**
 * Výběr data a času (náhrada za <input type="datetime-local"> na počítači).
 * Hodnota je stejná jako u nativního pole: "YYYY-MM-DDTHH:MM".
 *
 * Proč vlastní: v prohlížeči se sloupce hodin a minut točí dokola — kdo přejede
 * 00, musí listovat celé kolo. Tady sloupce končí na 23 h / 59 min a nahoře
 * na 0 h / 00 min. Na telefonu zůstává systémový výběr (je pro prst lepší).
 *
 * Okno se kreslí do <body>, aby ho neořízl formulář s posuvníkem.
 */
const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const DNY = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const pad = (n) => String(n).padStart(2, "0");
const HODINY = Array.from({ length: 24 }, (_, i) => i);
const MINUTY = Array.from({ length: 60 }, (_, i) => i);
const POLOZKA = 34;

const rozlozit = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v || "");
  if (!m) { const d = new Date(); return { r: d.getFullYear(), mes: d.getMonth(), den: d.getDate(), h: 9, min: 0 }; }
  return { r: +m[1], mes: +m[2] - 1, den: +m[3], h: +m[4], min: +m[5] };
};
const slozit = ({ r, mes, den, h, min }) => `${r}-${pad(mes + 1)}-${pad(den)}T${pad(h)}:${pad(min)}`;

function Sloupec({ hodnoty, vybrana, onVyber, popis, testid }) {
  const ref = useRef(null);
  // Vybraná hodnota doprostřed — jen při otevření, pak už se sloupcem hýbe uživatel.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = Math.max(0, vybrana * POLOZKA - el.clientHeight / 2 + POLOZKA / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div ref={ref} role="listbox" aria-label={popis} data-testid={testid}
      className="w-[54px] h-[252px] overflow-y-auto overscroll-contain flex flex-col items-stretch gap-0.5 px-0.5 [scrollbar-width:thin]">
      {hodnoty.map((x) => (
        <button key={x} type="button" role="option" aria-selected={x === vybrana} onClick={() => onVyber(x)}
          style={{ minHeight: POLOZKA }}
          className={`rounded-[8px] amount text-[13.5px] font-600 transition-colors ${
            x === vybrana ? "bg-ink text-white" : "text-sec hover:bg-inputbg"}`}>
          {pad(x)}
        </button>
      ))}
    </div>
  );
}

export default function DatumCas({ value, onChange, min, className = "", "data-testid": testid, id }) {
  const mobil = useJeMobil();
  const [otevreno, setOtevreno] = useState(false);
  const [poloha, setPoloha] = useState(null);
  const p = rozlozit(value);
  const [pohled, setPohled] = useState({ r: p.r, mes: p.mes });
  const tlacitko = useRef(null);
  const okno = useRef(null);

  const zmerit = () => {
    const r = tlacitko.current?.getBoundingClientRect();
    if (!r) return;
    const vyska = 340, sirka = 420;
    const dole = window.innerHeight - r.bottom;
    const nahoru = dole < vyska + 12 && r.top > dole;
    setPoloha({ left: Math.max(8, Math.min(r.left, window.innerWidth - sirka - 8)),
      top: nahoru ? Math.max(8, r.top - vyska - 6) : r.bottom + 6 });
  };
  const otevri = () => { zmerit(); setPohled({ r: p.r, mes: p.mes }); setOtevreno(true); };

  useEffect(() => {
    if (!otevreno) return undefined;
    const klik = (e) => {
      if (okno.current?.contains(e.target) || tlacitko.current?.contains(e.target)) return;
      setOtevreno(false);
    };
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); setOtevreno(false); } };
    document.addEventListener("mousedown", klik);
    document.addEventListener("keydown", esc, true);
    window.addEventListener("resize", zmerit);
    return () => {
      document.removeEventListener("mousedown", klik);
      document.removeEventListener("keydown", esc, true);
      window.removeEventListener("resize", zmerit);
    };
  }, [otevreno]);

  const nastav = (zmena) => onChange?.(slozit({ ...p, ...zmena }));

  if (mobil) {
    return (
      <input id={id} type="datetime-local" className={className} value={value} min={min}
        onChange={(e) => onChange?.(e.target.value)} data-testid={testid} />
    );
  }

  // mřížka měsíce od pondělí
  const prvni = new Date(pohled.r, pohled.mes, 1);
  const zacatek = new Date(prvni); zacatek.setDate(1 - ((prvni.getDay() + 6) % 7));
  const dny = Array.from({ length: 42 }, (_, i) => { const d = new Date(zacatek); d.setDate(zacatek.getDate() + i); return d; });
  const dnes = new Date();
  const minDen = min ? min.slice(0, 10) : null;
  const klicDne = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const posun = (o) => setPohled(({ r, mes }) => { const d = new Date(r, mes + o, 1); return { r: d.getFullYear(), mes: d.getMonth() }; });

  return (
    <>
      {/* skryté nativní pole — kvůli testům a vyplňování (fill), hodnota je stejná */}
      <input type="datetime-local" className="sr-only" tabIndex={-1} aria-hidden="true" value={value} min={min}
        onChange={(e) => onChange?.(e.target.value)} data-testid={testid} />
      <button ref={tlacitko} id={id} type="button" onClick={() => (otevreno ? setOtevreno(false) : otevri())}
        aria-haspopup="dialog" aria-expanded={otevreno}
        className={`${className} !flex items-center justify-between gap-2 text-left amount`}
        style={otevreno ? { borderColor: "#ff6a13", boxShadow: "0 0 0 3px rgba(255,106,19,.15)" } : undefined}>
        <span>{p.den}. {p.mes + 1}. {p.r} <span className="ml-1.5">{pad(p.h)}:{pad(p.min)}</span></span>
        <CalendarDays size={16} className="text-muted shrink-0" />
      </button>
      {otevreno && poloha && createPortal(
        <div ref={okno} role="dialog" aria-label="Vybrat datum a čas"
          className="vyber-menu fixed z-[200] w-[420px] bg-white rounded-card border border-line-softer shadow-panel p-3 flex gap-3"
          style={{ left: poloha.left, top: poloha.top }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-2">
              <span className="t-telo font-700 text-ink flex-1 pl-1">{MESICE[pohled.mes]} {pohled.r}</span>
              <button type="button" onClick={() => posun(-1)} aria-label="Předchozí měsíc"
                className="h-8 w-8 rounded-full hover:bg-inputbg inline-flex items-center justify-center"><ChevronLeft size={16} /></button>
              <button type="button" onClick={() => posun(1)} aria-label="Další měsíc"
                className="h-8 w-8 rounded-full hover:bg-inputbg inline-flex items-center justify-center"><ChevronRight size={16} /></button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {DNY.map((d) => <span key={d} className="label-up text-muted py-1">{d}</span>)}
              {dny.map((d) => {
                const k = klicDne(d);
                const vybrany = d.getFullYear() === p.r && d.getMonth() === p.mes && d.getDate() === p.den;
                const mimo = d.getMonth() !== pohled.mes;
                const zakazany = minDen && k < minDen;
                const jeDnes = k === klicDne(dnes);
                return (
                  <button key={k} type="button" disabled={zakazany}
                    onClick={() => { nastav({ r: d.getFullYear(), mes: d.getMonth(), den: d.getDate() }); if (mimo) setPohled({ r: d.getFullYear(), mes: d.getMonth() }); }}
                    className={`h-8 rounded-[8px] amount text-[12.5px] font-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                      vybrany ? "bg-ink text-white" : jeDnes ? "text-accent hover:bg-inputbg" : mimo ? "text-muted-2 hover:bg-inputbg" : "text-ink hover:bg-inputbg"}`}>
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-line-soft">
              <button type="button" onClick={() => { const d = new Date(); nastav({ r: d.getFullYear(), mes: d.getMonth(), den: d.getDate() }); setPohled({ r: d.getFullYear(), mes: d.getMonth() }); }}
                className="t-meta font-700 text-accent hover:text-accent-hover px-1">Dnes</button>
              <button type="button" onClick={() => setOtevreno(false)} className="btn-primary !h-8 !px-3.5 !text-[12.5px]">Hotovo</button>
            </div>
          </div>
          <div className="flex gap-1 border-l border-line-soft pl-2">
            <div className="flex flex-col items-center gap-1">
              <span className="label-up text-muted">hod</span>
              <Sloupec hodnoty={HODINY} vybrana={p.h} onVyber={(h) => nastav({ h })} popis="Hodiny" testid="datumcas-hodiny" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="label-up text-muted">min</span>
              <Sloupec hodnoty={MINUTY} vybrana={p.min} onVyber={(min) => nastav({ min })} popis="Minuty" testid="datumcas-minuty" />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
